import useWindowSize from '~/hooks/useWindowSize';
import * as React from 'react';
import useStorageState from '../../hooks/useStorageState';

interface MenuContextValue {
  isOpen: boolean;
  toggle: () => void;
  closeMenu: () => void;
  menuMinimized: boolean;
}

const MenuContext = React.createContext<MenuContextValue | undefined>(undefined);

export const MenuProvider = ({ children }: { children: React.ReactNode }) => {
  const { isMobile } = useWindowSize();
  const [isOpen, setIsOpen] = useStorageState('miniMenu', !isMobile, 'local');

  const toggle = React.useCallback(() => {
    setIsOpen(!isOpen);
  }, [setIsOpen, isOpen]);

  const closeMenu = React.useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  React.useEffect(() => {
    if (isMobile) {
      // close menu when transitioning to mobile
      closeMenu();
    }
  }, [isMobile, closeMenu]);

  const menuMinimized = !isOpen && isMobile;

  const value = React.useMemo(
    () => ({ isOpen, toggle, closeMenu, menuMinimized }),
    [isOpen, toggle, closeMenu, menuMinimized],
  );

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
};

const useMenu = () => {
  const context = React.useContext(MenuContext);

  if (!context) {
    throw new Error('useMenu must be used within MenuProvider');
  }
  return context;
};

export default useMenu;
